import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Icon } from './Icon';

/* ---------- Buttons: large, touch-friendly targets ---------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'md' | 'lg' | 'sm';

const BTN_BASE =
  'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 disabled:opacity-50 disabled:cursor-not-allowed';

const BTN_VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-accent-ink hover:bg-accent-strong active:bg-accent-strong shadow-[0_10px_24px_-14px_rgba(36,152,232,0.5)]',
  secondary:
    'bg-panel-2 text-slate-700 border border-slate-200 hover:border-slate-300 hover:text-slate-900 active:bg-slate-100',
  ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 active:bg-slate-200',
  danger: 'bg-danger text-white hover:bg-danger-hover active:bg-danger-active',
};

const BTN_SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-14 px-6 text-base', // tablet-friendly primary actions
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={`${BTN_BASE} ${BTN_VARIANTS[variant]} ${BTN_SIZES[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/* ---------- Surfaces ---------- */

export function Card({
  children,
  className = '',
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={`rounded-2xl border border-slate-200 bg-panel shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.04)] ${className}`}
    >
      {children}
    </div>
  );
}

/* ---------- Badges (roles, statuses, branches) ---------- */

type BadgeTone = 'slate' | 'indigo' | 'emerald' | 'amber' | 'rose';

const BADGE_TONES: Record<BadgeTone, string> = {
  slate: 'bg-slate-100 text-slate-700 ring-slate-200',
  indigo: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
  emerald: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  amber: 'bg-amber-100 text-amber-700 ring-amber-200',
  rose: 'bg-rose-100 text-rose-700 ring-rose-200',
};

export function Badge({
  tone = 'slate',
  children,
  className = '',
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${BADGE_TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/* ---------- Spinner ---------- */

export function Spinner({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

/* ---------- Form field ---------- */

export const inputCls =
  'w-full h-12 rounded-xl border border-slate-200 bg-white px-3.5 text-base text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25';

export function Field({
  label,
  htmlFor,
  error,
  hint,
  required = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string | undefined;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-slate-600">
        {label}
        {required && (
          <span className="ml-0.5 text-rose-500" aria-hidden="true">
            *
          </span>
        )}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && (
        <p className="mt-1 flex items-center gap-1 text-sm text-rose-600">
          <Icon name="alert" className="h-4 w-4 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

/* ---------- Modal (tap outside or X to close) ---------- */

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="animate-pop max-h-[92dvh] w-full overflow-y-auto rounded-2xl border border-slate-200 bg-panel shadow-2xl max-w-lg">
        <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-panel px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
          >
            <Icon name="x" className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="sticky bottom-0 flex justify-end gap-3 border-t border-slate-200 bg-panel px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- Pagination footer ---------- */

/**
 * Accurate record count + page navigation for server-paginated lists.
 * The count always shows the SERVER total (`of 137`), never just the
 * currently loaded rows, so a long list is never silently truncated.
 */
export function Pager({
  total,
  offset,
  loaded,
  onPrev,
  onNext,
  busy = false,
}: {
  total: number;
  offset: number;
  loaded: number;
  onPrev: () => void;
  onNext: () => void;
  busy?: boolean;
}) {
  const start = loaded === 0 ? 0 : offset + 1;
  const end = offset + loaded;
  // Past-the-end page (e.g. the last matching row was cancelled or deleted):
  // say so plainly and let Previous return to real records, instead of
  // rendering a confusing "Showing 51–50 of 50".
  const label =
    total === 0
      ? 'No records'
      : loaded === 0
        ? `This page is empty — ${total} record${total === 1 ? '' : 's'} exist${total === 1 ? 's' : ''} on other pages`
        : `Showing ${start}–${end} of ${total}`;
  return (
    <div
      role="navigation"
      aria-label="Pagination"
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm text-slate-600"
    >
      <span aria-live="polite" className="tabular-nums">
        {label}
      </span>
      <span className="flex gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={onPrev}
          disabled={busy || offset <= 0}
          aria-label={`Previous page (showing ${start}–${end} of ${total})`}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={onNext}
          disabled={busy || offset + loaded >= total}
          aria-label={`Next page (showing ${start}–${end} of ${total})`}
        >
          Next
        </Button>
      </span>
    </div>
  );
}

/* ---------- Error banner ---------- */

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{message}</span>
    </div>
  );
}